import { Component, Prop, Vue } from 'vue-property-decorator'
import BillingCardComponent from '@/modules/billing-module/shared/components/card/billing-card.vue'
import { BillableDeliveryNoteResponse, BillableDeliveryNoteStatus } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { CustomerResponse } from '@/api/services/billing/common-customer/billing-common-customer.dto'

@Component({
    components: {
        BillingCardComponent
    }
})
export default class BillingModalDeliveryNotesCrudCard extends Vue {
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConcepts!: AppliedBillableConceptResponse[]
    @Prop({ type: Object }) readonly deliveryNote!: Nullable<BillableDeliveryNoteResponse>
    @Prop({ type: Object }) readonly customer!: Nullable<CustomerResponse>

    get title(): string {
        return this.deliveryNote?.label ?? this.$tc('accounting.field.newDeliveryNote')
    }

    get statusName(): string | undefined {
        return this.deliveryNote?.status.translatedName
    }
    get statusClass(): StrictNullable<string> {
        if (this.deliveryNote) {
            switch (this.deliveryNote.status.code) {
            case BillableDeliveryNoteStatus.GENERATED:
                return 'state-primary'
            case BillableDeliveryNoteStatus.ISSUED:
                return 'state-alt-success-2'
            case BillableDeliveryNoteStatus.VERIFIED:
                return 'state-success'
            }
        }
        return null
    }

    get customerName(): string | undefined {
        let customer = this.deliveryNote?.customer
        if (!customer && this.appliedBillableConcepts.length) {
            customer = this.appliedBillableConcepts[0].customer
        }
        if (!customer && this.customer) {
            customer = this.customer
        }
        return customer?.name
    }

    get dateLabel(): string {
        if (this.deliveryNote) {
            switch (this.deliveryNote.status.code) {
            case BillableDeliveryNoteStatus.GENERATED:
                return 'accounting.field.generationDate'
            case BillableDeliveryNoteStatus.ISSUED:
                return 'accounting.field.issueDate'
            case BillableDeliveryNoteStatus.VERIFIED:
                return 'accounting.field.verificationDate'
            }
        }
        return 'accounting.field.generationDate'
    }
    get dateValue(): StrictNullable<string> {
        if (this.deliveryNote) {
            switch (this.deliveryNote.status.code) {
            case BillableDeliveryNoteStatus.GENERATED:
                return this.deliveryNote.generationDate
            case BillableDeliveryNoteStatus.ISSUED:
                return this.deliveryNote.issueDate
            case BillableDeliveryNoteStatus.VERIFIED:
                return this.deliveryNote.verificationDate
            }
        }
        return null
    }

    get responsibleLabel(): string {
        if (this.deliveryNote) {
            switch (this.deliveryNote.status.code) {
            case BillableDeliveryNoteStatus.GENERATED:
                return 'accounting.field.generationResponsible'
            case BillableDeliveryNoteStatus.ISSUED:
                return 'accounting.field.issueResponsible'
            case BillableDeliveryNoteStatus.VERIFIED:
                return 'accounting.field.verificationResponsible'
            }
        }
        return 'accounting.field.generationResponsible'
    }
    get responsibleName(): string {
        if (this.deliveryNote) {
            switch (this.deliveryNote.status.code) {
            case BillableDeliveryNoteStatus.GENERATED:
                return this.deliveryNote.generator.fullName
            case BillableDeliveryNoteStatus.ISSUED:
                return this.deliveryNote.issuer?.fullName ?? '-'
            case BillableDeliveryNoteStatus.VERIFIED:
                return this.deliveryNote.verifier?.fullName ?? '-'
            }
        }
        return '-'
    }

    get accountingUnitSymbol(): string | undefined {
        let accountingUnit = this.deliveryNote?.conceptsAccountingUnit
        if (
            !accountingUnit &&
            this.appliedBillableConcepts.length &&
            this.appliedBillableConcepts[0].rate
        ) {
            accountingUnit = this.appliedBillableConcepts[0].rate.accountingUnit
        }
        return accountingUnit?.symbol
    }
    get conceptsTotalValue(): number {
        if (this.deliveryNote) {
            return this.deliveryNote.conceptsTotalValue ?? 0
        }
        return this.appliedBillableConcepts.reduce((accumulator, concept) => {
            return accumulator + (concept.value ?? 0)
        }, 0)
    }
}
